import * as RSwitch from '@radix-ui/react-switch';
import type { ReactNode } from 'react';
import { cn } from './cn';

export interface SwitchProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  /** antd: text shown inside the track when on/off. */
  checkedChildren?: ReactNode;
  unCheckedChildren?: ReactNode;
  size?: 'small' | 'default';
  loading?: boolean;
  title?: string;
  'aria-label'?: string;
}

/** Toggle switch (replaces antd Switch) built on Radix Switch. */
export function Switch({
  checked, onCheckedChange, disabled, className, checkedChildren, unCheckedChildren, size, loading, title, ...rest
}: SwitchProps) {
  const small = size === 'small';
  const hasText = checkedChildren != null || unCheckedChildren != null;
  return (
    <RSwitch.Root
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled || loading}
      title={title}
      aria-label={rest['aria-label']}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-pill bg-line transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:opacity-50',
        'data-[state=checked]:bg-brand-600',
        small ? 'h-[18px]' : 'h-[22px]',
        // Fixed track widths so the thumb's pixel travel below lands exactly at the right edge.
        hasText ? (small ? 'w-[46px]' : 'w-[54px]') : (small ? 'w-[32px]' : 'w-[40px]'),
        className,
      )}
    >
      {hasText && (
        <span
          className={cn(
            'pointer-events-none absolute select-none whitespace-nowrap font-medium text-white',
            small ? 'text-[9px]' : 'text-[10px]',
            // Text sits opposite the thumb: right side when off, left side when on.
            checked ? 'left-1.5' : 'right-1.5',
          )}
        >
          {checked ? checkedChildren : unCheckedChildren}
        </span>
      )}
      <RSwitch.Thumb
        className={cn(
          'block rounded-pill bg-white shadow transition-transform',
          small ? 'size-[14px]' : 'size-[18px]',
          // Thumb travel = trackWidth − thumbWidth − 2px right gap (fixed px so it lands flush right).
          hasText
            ? (small ? 'translate-x-0.5 data-[state=checked]:translate-x-[30px]' : 'translate-x-0.5 data-[state=checked]:translate-x-[34px]')
            : (small ? 'translate-x-0.5 data-[state=checked]:translate-x-[16px]' : 'translate-x-0.5 data-[state=checked]:translate-x-[20px]'),
        )}
      />
    </RSwitch.Root>
  );
}
